import { Components } from "appworks/components/components";
import { AbstractSceneTransition } from "appworks/graphics/layers/scene-transitions/abstract-scene-transition";
import { gameState } from "appworks/model/game-state";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { fadeIn2, fadeOut2 } from "appworks/utils/animation/fade2";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Timer } from "appworks/utils/timer";
import { gameLayers } from "game-layers";
import { SlingoCompletionReason } from "slingo/integration/slingo-schema";
import { SlingoGameProgressResult } from "slingo/model/results/slingo-game-progress-result";
import { slingoModel } from "slingo/model/slingo-model";
import { BigWinComponent } from "slotworks/components/bigwin/big-win-component";

export class FowlGameSummaryWinTransition extends AbstractSceneTransition {
    public in(): Contract<void> {
        return this.helper(true);
    }

    public out(): Contract<void> {
        return this.helper(false);
    }

    protected helper(transitionIn: boolean): Contract<void> {
        const gameplay = gameState.getCurrentGame();
        const totalWin = gameplay.getTotalWin();
        const totalStake = gameplay.getTotalWagered();
        const winThreshold = slingoModel.read().gameConfig.celebrateWinThreshold; // percentage, i.e. 100 for a 1x minimum
        const meetsWinRequirement = (totalWin / gameplay.getTotalWagered()) * 100 >= winThreshold;

        const isBigWin = Components.get(BigWinComponent).isBigWin(totalWin, totalStake);
        const isFullHouse = gameplay.getLatestResultOfType(SlingoGameProgressResult).completionReason === SlingoCompletionReason.TICKET_MATCHED;

        let pref = "result";
        if (isFullHouse && meetsWinRequirement) {
            pref = "megawin";
        } else if (isBigWin && meetsWinRequirement) {
            pref = "bigwin";
        } else if (meetsWinRequirement) {
            pref = "win";
        }

        if (transitionIn) {
            const sound = Services.get(SoundService);
            if (meetsWinRequirement) {
                sound.customEvent("results_buildup");
                Timer.setTimeout(() => sound.customEvent("results_reveal"), 1800);
            } else {
                sound.customEvent("results_below_staked");
            }
        }

        const { title, total_win_label, total_win, frame, win_breakdown } = gameLayers.Prompts.scene.gamesummary_win.contents;

        const animationName = `${pref}_${transitionIn ? "in" : "out"}`;
        const targetAnimation = frame.getAnimations().find(x => x.name === animationName);
        return new Parallel([
            () => frame.playOnce(animationName),
            // NOTE: we are trying to fade in the labels and sync the timing to the animation.
            // We are getting the animation duration (which is in seconds), and converting it to ms
            // before passing it to the fade tween.
            () =>
                transitionIn
                    ? // NOTE: on the _in_ transition, only want to show the title, as the other two labels are revealed separately
                      // in the game over state component. Also since the intro animation is fairly long, we want the title to be fully
                      // revealed partway through the animation (in this case about 10% through the animation).
                      fadeIn2(title, targetAnimation.duration * 100)
                    : fadeOut2([title, total_win, total_win_label, win_breakdown], targetAnimation.duration * 1000),
        ]);
    }
}
